import styles from './index.module.scss';
import {
  Button,
  Form,
  FormTitle,
  InputNumber,
  PopConfirm,
  Select,
  SubContent,
  Table,
  Wrapper,
  useBreadcrumbRoutes,
  Tag,
  Modal,
  message,
} from '@maxtropy/components';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Space } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { AccumTypeOptions, MeasurementType, CompareType, CircuitType } from './utils';
import BindUnitModal from './BindUnitModal';
import { isEmpty } from 'lodash-es';
import {
  apiV2HighEnergyDeviceGetPropertyListPost,
  V2HighEnergyDeviceGetPropertyListPostResponse,
  apiV2HighEnergyDeviceDetailPost,
  V2HighEnergyDeviceDetailPostResponse,
  apiV2HighEnergyDeviceGetDeviceListPost,
  V2HighEnergyDeviceGetDeviceListPostResponse,
  apiV2HighEnergyDeviceGetIndicatorBaseLinePost,
  apiV2HighEnergyDeviceSavePost,
} from '@maxtropy/device-customer-apis-v2';
const routes = [{ name: '高能耗设备监控配置' }];
type StatusConfigItem = V2HighEnergyDeviceDetailPostResponse['statusConfig'];
type PropertyOpItem = V2HighEnergyDeviceGetPropertyListPostResponse['list'];
type KeyIndicatorsItem = V2HighEnergyDeviceDetailPostResponse['keyIndicators'];

interface OptionType {
  label: string;
  value: string;
}
const stateResDealWith = (res: StatusConfigItem) => {
  return [
    {
      ...res,
      deviceName: `${res?.deviceName}-${res?.deviceCode}`,
      timeResolution: Number(res?.timeResolution),
    },
  ];
};

const HighEnergy: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<V2HighEnergyDeviceDetailPostResponse>();
  const [showEnergyUnitModal, setShowEnergyUnitModal] = useState<boolean>(false);
  const [unitId, setUnitId] = useState<number>();
  const [nodeName, setNodeName] = useState<string>();

  const [meterOp, setMeterOp] = useState<V2HighEnergyDeviceGetDeviceListPostResponse['list']>();
  const [equiOp, setEquiOp] = useState<V2HighEnergyDeviceGetDeviceListPostResponse['list']>();
  // const [propertyOp, setPropertyOp] = useState<Map<number, PropertyOpItem>>(new Map());
  const [propertyOp, setPropertyOp] = useState<Record<string, PropertyOpItem>>({});
  const [baseLineOp, setBaseLineOp] = useState<V2HighEnergyDeviceGetDeviceListPostResponse['list']>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [showTag, setShowTag] = useState(false);
  const WatchKeyIndicator = Form.useWatch('keyIndicators', form);

  useEffect(() => {
    setLoading(true);
    apiV2HighEnergyDeviceDetailPost({ id }).then(res => {
      const stateListData = res && res.statusConfig ? stateResDealWith(res.statusConfig) : [{}];
      if (res) {
        const transformed = (res.keyIndicators || []).map((item: any) => ({
          ...item,
          propertyInfo: (item.propertyInfo || []).map((p: any) => ({
            ...p,
            value: p.propertyId,
            label: p.propertyName,
          })),
        }));

        form.setFieldsValue({
          // keyIndicators: res.keyIndicators || [],
          keyIndicators: transformed || [],
        });
        setData(res);
        setUnitId(res.energyUnitId);
        propertyChange(res.statusConfig?.deviceId);
      }

      form.setFieldsValue({
        statusConfig: stateListData,
      });
      setLoading(false);
    });
    // 关键指标设备
    apiV2HighEnergyDeviceGetDeviceListPost({ id, queryType: CompareType.INDICATOR }).then(res => {
      setEquiOp(res.list ?? []);
    });
    apiV2HighEnergyDeviceGetIndicatorBaseLinePost({ id }).then(res => {
      setBaseLineOp(res.list ?? []);
    });
  }, []);

  useEffect(() => {
    if (!unitId) return;
    isInitialMount ? setIsInitialMount(false) : form.setFieldsValue({ statusConfig: [{}] });
    apiV2HighEnergyDeviceGetDeviceListPost({ id: unitId, queryType: CompareType.DEVICE }).then(res => {
      setMeterOp(res.list ?? []);
    });
  }, [unitId]);

  const deviceChange = async (value: any, index: any) => {
    const tableData = form.getFieldValue('keyIndicators');
    tableData[index] = {
      ...tableData[index],
      deviceId: value,
      deviceName: equiOp?.find(d => d.id === value)?.name || '',
      propertyId: undefined,
      propertyName: undefined,
      propertyInfo: undefined,
      attrId: undefined,
      attrName: undefined,
    };

    form.setFieldsValue({ keyIndicators: tableData });
  };

  const propertyChange = useCallback(async (val: any) => {
    if (!!val) {
      const res = await apiV2HighEnergyDeviceGetPropertyListPost({ id: val });
      setPropertyOp(prev => ({
        ...prev,
        [val]: res.list,
      }));
    }
  }, []);

  const handleTreeSelect = (info: any) => {
    setUnitId(info.key);
    setNodeName(info.label);
  };

  const addRow = () => {
    const newRow = {
      id: uuidv4(),
    };
    const prevList = form.getFieldValue('keyIndicators') || [];
    const newData = [...prevList, newRow];
    form.setFieldsValue({ keyIndicators: newData });
  };

  useEffect(() => {
    if (nodeName || data?.energyUnitName) {
      setShowTag(true);
    }
  }, [nodeName, data]);

  useEffect(() => {
    if (!showTag) {
      setUnitId(undefined);
      setMeterOp([]);
      form.setFieldsValue({ statusConfig: [{}] });
    }
  }, [showTag]);

  const stateColumns: ColumnsType<any> = [
    {
      title: '表计名称',
      dataIndex: 'deviceName',
      width: '30%',
      render(_, record, index) {
        return (
          <Form.Item
            name={['statusConfig', index, 'deviceName']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              placeholder="请选择"
              style={{ width: '100%' }}
              options={(meterOp ?? []).map(({ id, name, code }) => ({
                label: `${name}-${code}`,
                value: id,
              }))}
              showSearch
              allowClear
              optionFilterProp="label"
              onChange={(val, option) => {
                const tableData = form.getFieldValue('statusConfig');
                tableData[index] = {
                  ...tableData[index],
                  deviceId: val,
                  deviceName: (option as unknown as OptionType).label,
                  propertyId: undefined,
                  propertyName: undefined,
                };

                form.setFieldsValue({ statusConfig: tableData });
                propertyChange(val);
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '计量数据属性',
      dataIndex: 'propertyName',
      width: '30%',
      render(_, record, index) {
        const rowData = form.getFieldValue(['statusConfig', index]) || {};
        // const options = propertyOp.get(rowData.deviceId) || [];
        const options = propertyOp[record.deviceId] || [];

        return (
          <Form.Item
            name={['statusConfig', index, 'propertyName']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              allowClear
              showSearch
              optionFilterProp="label"
              options={options.map(({ id, name }: any) => ({ label: name, value: id }))}
              onChange={val => {
                const tableData = form.getFieldValue('statusConfig');
                const finslist = options.find((o: any) => o.id === val);
                tableData[index] = {
                  ...tableData[index],
                  propertyId: val,
                  propertyName: finslist?.name,
                  measurementType: finslist?.measurementType || '',
                  physicalUnitName: finslist?.unitName || '',
                };
                form.setFieldsValue({ statusConfig: tableData });
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: (
        <div>
          <span>停机用能上限</span>
          <span style={{ marginLeft: 100 }}>待机用能上限</span>
        </div>
      ),
      width: '40%',
      render(_, record, index) {
        return (
          <div className={styles.intervalCell}>
            {/* 停机用能上限 */}
            <Form.Item
              name={['statusConfig', index, 'shutdownThreshold']}
              style={{ marginBottom: 0 }}
              rules={[{ message: '请选择', required: true }]}
            >
              <InputNumber
                style={{ width: 140 }}
                min={0}
                onChange={() => {
                  form.validateFields([['statusConfig', index, 'standbyThreshold']]);
                }}
                max={999999999.9999}
                precision={4}
                placeholder="请输入"
              />
            </Form.Item>

            <span>～</span>

            {/* 待机用能上限 */}
            <Form.Item
              name={['statusConfig', index, 'standbyThreshold']}
              style={{ marginBottom: 0 }}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const { shutdownThreshold } = getFieldValue(['statusConfig', index]) || {};
                    if (value === undefined) return Promise.reject(new Error('请输入'));
                    if (shutdownThreshold !== undefined && value !== undefined && shutdownThreshold > value) {
                      return Promise.reject(new Error('停机用能上限需要小于待机用能上限'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                style={{ width: 140 }}
                min={0}
                onChange={() => {
                  form.validateFields([['statusConfig', index, 'shutdownThreshold']]);
                }}
                max={999999999.9999}
                precision={4}
                placeholder="请输入"
              />
            </Form.Item>

            <Form.Item dependencies={['statusConfig', index, 'propertyName']} noStyle>
              {({ getFieldValue }) => {
                const rowItem: StatusConfigItem = getFieldValue(['statusConfig', index]) || {};
                const curPropertyOp = (propertyOp[record.id] || []).find(
                  (property: any) => property.id === rowItem?.propertyId
                );
                const measurementType = curPropertyOp?.measurementType ?? MeasurementType.ACCUM;
                const physicalUnitName = (curPropertyOp as { unitName?: string } | undefined)?.unitName;

                return measurementType === MeasurementType.ACCUM ? (
                  <>
                    <span>{physicalUnitName ?? ''}/</span>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      name={['statusConfig', index, 'timeResolution']}
                      rules={[{ message: '请选择', required: true }]}
                    >
                      <Select style={{ width: 100 }} placeholder="请选择" options={AccumTypeOptions} />
                    </Form.Item>
                  </>
                ) : (
                  <span>{physicalUnitName ?? ''}</span>
                );
              }}
            </Form.Item>
          </div>
        );
      },
    },
  ];

  const kpiColumns: ColumnsType<any> = [
    {
      title: '设备选择',
      dataIndex: 'deviceName',
      width: '30%',
      render(_, record, index) {
        return (
          <Form.Item
            name={['keyIndicators', index, 'deviceName']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              allowClear
              showSearch
              optionFilterProp="label"
              options={(equiOp ?? []).map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
              onChange={val => {
                deviceChange(val, index);
                propertyChange(val);
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '数据属性',
      dataIndex: 'propertyInfo',
      width: '30%',
      render(_, record, index) {
        const options = propertyOp[record.deviceId] || [];
        const rowValue = form.getFieldValue(['keyIndicators', index, 'propertyInfo']);
        const rowType = rowValue?.[0]?.groupType;
        // console.log(options, 'options');

        return (
          <Form.Item
            name={['keyIndicators', index, 'propertyInfo']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              allowClear
              showSearch
              mode={rowType !== CircuitType.NOGROUPING ? 'multiple' : undefined}
              optionFilterProp="label"
              labelInValue
              onFocus={() => propertyChange(record.deviceId)}
              onChange={val => {
                // 拿到现在正在选择 然后进行判断
                const tableData = form.getFieldValue('keyIndicators');
                const valArray = Array.isArray(val) ? val : [val];
                const merged = valArray.map((v: any) => {
                  const type = options.find(i => i.id === v.value);
                  // console.log(type?.measurementType, 'type');

                  return {
                    ...v,
                    measurementType: type?.measurementType,
                    groupType: type?.groupType,
                  };
                });
                tableData[index] = {
                  ...tableData[index],
                  propertyInfo: merged,
                };
                form.setFieldsValue({ keyIndicators: tableData });
              }}
              options={options.map((o: any) => {
                // 全局禁用逻辑：别的行已经选过的 propertyId 禁用
                // 已经选过的    和类型限制要做
                let other;
                let typeDisabled;

                const alreadySelected = selectedIds.includes(o.id) && !rowValue?.some((v: any) => v.value === o.id);

                if (rowType && rowType !== CircuitType.NOGROUPING) {
                  other = rowType;
                  // typeDisabled = rowValue === other ? o.groupType !== other : rowType ? o.groupType !== rowType : false;
                  typeDisabled = o.groupType !== rowType;
                } else {
                  typeDisabled = false;
                }

                return {
                  label: o.name,
                  value: o.id,
                  disabled: alreadySelected || typeDisabled,
                  // groupType: o.groupType,
                };
              })}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '基准线',
      dataIndex: 'attrName',
      width: '30%',
      render(_, record, index) {
        return (
          <Form.Item name={['keyIndicators', index, 'attrName']} style={{ marginBottom: 0 }}>
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              allowClear
              onChange={val => {
                const tableData = form.getFieldValue('keyIndicators');
                tableData[index] = {
                  ...tableData[index],
                  attrId: val ?? undefined,
                  attrName: val ? baseLineOp?.find(d => d.id === val)?.name : undefined,
                };
                form.setFieldsValue({ keyIndicators: tableData });
              }}
              options={(baseLineOp ?? []).map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'option',
      width: '10%',
      render: (_, record, index) => {
        return (
          <PopConfirm
            title="您是否删除？"
            onConfirm={() => {
              const currentList: KeyIndicatorsItem[] = form.getFieldValue('keyIndicators') || [];
              const newList = currentList.filter((_, i) => i !== index);
              form.setFieldsValue({ keyIndicators: newList });
            }}
          >
            <Button type="link">删除</Button>
          </PopConfirm>
        );
      },
    },
  ];

  const selectedIds = useMemo(() => {
    const values = form.getFieldValue('keyIndicators');
    if (!values) return;
    const res =
      values
        .flatMap((row: any) => {
          if (!isEmpty(row?.propertyInfo) && row?.propertyInfo[0].groupType === 0) {
            return row?.propertyInfo?.map((p: any) => p.value);
          }
        })
        .filter(Boolean) || [];
    return res;
  }, [WatchKeyIndicator]);

  const onFinish = async (values: any) => {
    const vali = await form.validateFields();
    const res =
      vali.keyIndicators
        .map((row: any) => {
          return row?.propertyInfo?.map((p: any) => p.value);
        })
        .filter(Boolean) || [];

    if (hasDuplicate(res)) {
      message.error('指标配置数据属性存在重复，请检查后提交！');
      return;
    }

    const statusRes = {
      ...values.statusConfig[0],
      deviceName: values.statusConfig[0].deviceName.split('-')[0],
    };

    const IndicatorsRes = values.keyIndicators.map((item: any) => ({
      ...item,
      propertyInfo: (item.propertyInfo || []).map((p: any) => ({
        propertyId: p.value,
        propertyName: p.label,
        measurementType: p.measurementType,
        groupType: p.groupType,
      })),
      groupType: item.propertyInfo[0].groupType,
    }));
    // console.log(IndicatorsRes, statusRes, '查看叉掉数据还在');

    apiV2HighEnergyDeviceSavePost({
      statusConfig: statusRes,
      keyIndicators: IndicatorsRes,
      inventoryId: Number(id),
      energyUnitId: unitId ?? data?.energyUnitId,
    }).then(res => {
      navigate(`/asset/equipment/ledger`);
    });
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} className={styles.wrapper}>
      <FormTitle title="高能耗设备监测配置"></FormTitle>
      <SubContent>
        <Form form={form} layout="vertical" isOrigin onFinish={onFinish}>
          <Form.Item name="energyUnitId" required label="用能计量">
            <div>
              <Button
                type="link"
                onClick={() => {
                  setShowEnergyUnitModal(true);
                }}
              >
                选择用能单元
              </Button>

              {showTag && (
                <Tag
                  closeIcon
                  onClose={() => {
                    setShowTag(false);
                  }}
                >
                  {nodeName ?? data?.energyUnitName}
                </Tag>
              )}
            </div>
          </Form.Item>

          <Form.Item name="statusConfig" valuePropName="dataSource" required label="资产设备状态配置">
            <Table columns={stateColumns} rowKey="deviceName" loading={loading} />
          </Form.Item>
          <Form.Item
            name="keyIndicators"
            valuePropName="dataSource"
            label="关键指标配置"
            style={{ marginBottom: '10px' }}
          >
            <Table columns={kpiColumns} rowKey="propertyName" loading={loading} />
          </Form.Item>

          <Button
            wrapStyle={{ width: '100%' }}
            style={{ width: '100%' }}
            type="dashed"
            onClick={addRow}
            ghost
            icon={<PlusOutlined />}
          >
            新建配置
          </Button>
          <Space size={8} className="sticky-footer-left">
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button
              onClick={() => {
                Modal.confirm({
                  title: '是否放弃所有未保存信息并返回列表？',
                  onOk: () => navigate(`/asset/equipment/ledger`),
                });
              }}
            >
              取消
            </Button>
          </Space>
        </Form>
      </SubContent>
      {showEnergyUnitModal && (
        <BindUnitModal setOpen={setShowEnergyUnitModal} onUnitIdSelect={handleTreeSelect} energyUnitIds={unitId} />
      )}
    </Wrapper>
  );
};

export default HighEnergy;

export function hasDuplicate(arr: number[][]): boolean {
  const seen = new Set();

  for (const group of arr) {
    // 排序后转成字符串，保证顺序无关
    const key = [...group].sort((a, b) => a - b).join(',');

    if (seen.has(key)) {
      return true; // 有重复
    }
    seen.add(key);
  }
  return false; // 没重复
}
